import { EsaMembershipCreditBalancesQuery } from "@outschool/gql-frontend-generated";
import { renderCurrencyString } from "@outschool/localization";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import React from "react";

export const esaMembershipCreditBalanceQuery = gql`
  query EsaMembershipCreditBalances {
    esaMembershipCreditBalances {
      ...EsaMembershipCreditBalancesFragment
    }
  }

  fragment EsaMembershipCreditBalancesFragment on EsaMembershipCreditBalance {
    uid
    balance
    openToBuy
    learner {
      uid
      name
      age
    }
    fundingProgram {
      abbreviation
      name
      isClassWallet
    }
  }
`;

export type EsaCreditBalance = {
  balance: number;
  openToBuy: number;
  age: number;
  name: string;
  learnerUid: string;
  programName: string;
  programAbbreviation: string;
  isClassWallet: boolean;
  uid: string;
};

export function formatEsaWalletCents(
  cents: number,
  showCurrencyCode: boolean = true,
) {
  return renderCurrencyString({
    priceInCents: cents,
    showCurrencyCode,
    displayNoCents: true,
  });
}

export const useEsaMembershipCreditBalances = () => {
  const { isLoggedIn } = useSession();

  const { data, loading, refetch } =
    useQueryWithPreviousData<EsaMembershipCreditBalancesQuery>(
      esaMembershipCreditBalanceQuery,
      {
        skip: !isLoggedIn,
      },
    );

  const creditBalances = React.useMemo(() => {
    const creditBalances = data?.esaMembershipCreditBalances ?? [];
    return creditBalances.map<EsaCreditBalance>(creditBalance => ({
      balance: creditBalance.balance,
      openToBuy: creditBalance.openToBuy,
      name: creditBalance.learner.name!,
      age: creditBalance.learner.age!,
      learnerUid: creditBalance.learner.uid!,
      programName: creditBalance.fundingProgram.name!,
      programAbbreviation: creditBalance.fundingProgram.abbreviation!,
      isClassWallet: creditBalance.fundingProgram.isClassWallet!,
      uid: creditBalance.uid,
    }));
  }, [data]);

  const totalBalanceCents = React.useMemo(
    () => creditBalances.reduce((sum, balance) => sum + balance.balance, 0),
    [creditBalances],
  );

  const totalOpenToBuyCents = React.useMemo(
    () => creditBalances.reduce((sum, balance) => sum + balance.openToBuy, 0),
    [creditBalances],
  );

  return {
    hasEsaMembershipCreditBalances: creditBalances.length > 0,
    creditBalances,
    totalBalanceCents,
    totalOpenToBuyCents,
    loading,
    refetch,
  };
};

type EsaMembershipCredit = ReturnType<typeof useEsaMembershipCreditBalances>;

export type EsaMembershipCreditBalance =
  EsaMembershipCredit["creditBalances"][number];
